import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { Report, PublicShaming, UserReportHistory, ReportingSettings } from '../types/reporting';

class ReportingService {
  private reportsCollection = 'reports';
  private publicShamingCollection = 'publicShaming';
  private userHistoryCollection = 'userReportHistory';
  private settingsCollection = 'reportingSettings';

  /**
   * Meld een ongepaste DM
   */
  async reportInappropriateMessage(
    reporterId: string,
    reporterName: string,
    reportedUserId: string,
    reportedUserName: string,
    messageContent: string,
    reason: string,
    category: Report['category'],
    severity: Report['severity'] = 'medium'
  ): Promise<string> {
    try {
      console.log('🚨 Creating report for inappropriate message');

      const now = new Date();
      const warningExpiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 uur

      const report: Omit<Report, 'id'> = {
        reporterId,
        reporterName,
        reportedUserId,
        reportedUserName,
        reportType: 'inappropriate_dm',
        messageContent,
        messageTimestamp: now,
        reason,
        category,
        severity,
        status: 'warning_sent',
        createdAt: now,
        warningExpiresAt
      };

      const docRef = await addDoc(collection(db, this.reportsCollection), {
        ...report,
        createdAt: serverTimestamp(),
        warningExpiresAt: Timestamp.fromDate(warningExpiresAt)
      });

      // Update user history
      await this.updateUserReportHistory(reporterId, 'reporter');
      await this.updateUserReportHistory(reportedUserId, 'reported');

      // Send warning to reported user (via inbox/notification system)
      await this.sendWarningNotification(reportedUserId, docRef.id, messageContent);

      // Schedule auto-publish after 24 hours
      await this.scheduleAutoPublish(docRef.id, warningExpiresAt);

      console.log('✅ Report created successfully:', docRef.id);
      return docRef.id;

    } catch (error) {
      console.error('❌ Error creating report:', error);
      throw error;
    }
  }

  /**
   * Meld een post als A.I.-content zonder vermelding
   * Na 3 unieke meldingen op dezelfde post wordt de post geforceerd gelabeld
   * en ontvangt de auteur een waarschuwing (strike-systeem).
   */
  async reportAIMislabeling(
    reporterId: string,
    reporterName: string,
    reportedUserId: string,
    reportedUserName: string,
    postId: string,
    postContent: string,
  ): Promise<{ reportId: string; totalReports: number; strikeIssued: boolean }> {
    try {
      const now = new Date();

      // Prevent duplicate reports by same user on same post
      const existingQuery = query(
        collection(db, this.reportsCollection),
        where('reportType', '==', 'ai_mislabeling'),
        where('postId', '==', postId),
        where('reporterId', '==', reporterId)
      );
      const existingSnap = await getDocs(existingQuery);
      if (!existingSnap.empty) {
        const existing = existingSnap.docs[0];
        return { reportId: existing.id, totalReports: 0, strikeIssued: false };
      }

      const report: Omit<Report, 'id'> = {
        reporterId,
        reporterName,
        reportedUserId,
        reportedUserName,
        reportType: 'ai_mislabeling',
        postId,
        messageContent: postContent.substring(0, 500),
        messageTimestamp: now,
        reason: 'Post bevat A.I.-content zonder vermelding',
        category: 'ai_mislabeling',
        severity: 'medium',
        status: 'pending',
        createdAt: now,
      };

      const docRef = await addDoc(collection(db, this.reportsCollection), {
        ...report,
        createdAt: serverTimestamp(),
      });

      // Count total unique reports on this post
      const allReportsQuery = query(
        collection(db, this.reportsCollection),
        where('reportType', '==', 'ai_mislabeling'),
        where('postId', '==', postId)
      );
      const allReportsSnap = await getDocs(allReportsQuery);
      const totalReports = allReportsSnap.size;

      // After 3 unique reports: force-label post + issue strike
      let strikeIssued = false;
      if (totalReports >= 3) {
        try {
          const { updateDoc: firestoreUpdate, doc: firestoreDoc } = await import('firebase/firestore');
          // Force AI label on the post
          await firestoreUpdate(firestoreDoc(db, 'communityPosts', postId), {
            aiDisclosure: 'full',
            aiDisclosureForcedAt: serverTimestamp(),
            aiDisclosureForcedReason: 'Gemeld door meerdere gebruikers',
          });
          // Issue strike to the user
          await this.issueAIMislabelingStrike(reportedUserId, reportedUserName, postId);
          strikeIssued = true;

          // Mark all reports on this post as published
          await Promise.all(
            allReportsSnap.docs.map((d) =>
              updateDoc(doc(db, this.reportsCollection, d.id), { status: 'published' })
            )
          );
        } catch (e) {
          console.error('Force-label/strike failed:', e);
        }
      }

      await this.updateUserReportHistory(reporterId, 'reporter');

      return { reportId: docRef.id, totalReports, strikeIssued };
    } catch (error) {
      console.error('❌ Error reporting AI mislabeling:', error);
      throw error;
    }
  }

  /**
   * Verwerk een A.I.-label overtreding strike voor de opgegeven gebruiker.
   * Strike-escalatie:
   *   1e overtreding → waarschuwing + post geforceerd gelabeld
   *   2e overtreding → 7 dagen postingverbod
   *   3e overtreding → 30 dagen postingverbod
   *   4e+ overtreding → account-ban (review door admin)
   */
  private async issueAIMislabelingStrike(userId: string, userName: string, postId: string): Promise<void> {
    try {
      const history = await this.getUserReportHistory(userId);
      const strikes = (history?.aiMislabelingStrikes || 0) + 1;

      let suspendUntil: Date | null = null;
      let isBanned = false;
      let banReason = '';

      if (strikes === 2) {
        suspendUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      } else if (strikes === 3) {
        suspendUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      } else if (strikes >= 4) {
        isBanned = true;
        banReason = `Automatische ban: ${strikes}× A.I.-content niet vermeld`;
      }

      const historyRef = doc(db, this.userHistoryCollection, userId);
      await updateDoc(historyRef, {
        aiMislabelingStrikes: strikes,
        ...(suspendUntil ? { aiPostingBannedUntil: Timestamp.fromDate(suspendUntil) } : {}),
        ...(isBanned ? { isBanned: true, banReason } : {}),
        updatedAt: serverTimestamp(),
      });

      // Inbox notification to the offender
      await addDoc(collection(db, 'user_inbox', userId, 'messages'), {
        type: 'warning',
        category: 'moderation',
        title: `⚠️ A.I.-content overtreding (${strikes}× gemeld)`,
        message: strikes === 1
          ? `Een van jouw posts is door meerdere gebruikers gemeld als A.I.-content zonder vermelding. De post is automatisch voorzien van een A.I.-label. Dit is je eerste waarschuwing. Vergeet bij volgende posts niet aan te geven of content A.I.-gegenereerd is.`
          : strikes === 2
          ? `Dit is de 2e keer dat je A.I.-content niet hebt vermeld. Je kunt 7 dagen lang niet posten.`
          : strikes === 3
          ? `3e overtreding: posting is 30 dagen geblokkeerd.`
          : `Je account staat onder review vanwege herhaalde A.I.-label overtredingen.`,
        isRead: false,
        priority: strikes >= 3 ? 'high' : 'medium',
        createdAt: serverTimestamp(),
        relatedPostId: postId,
      });

      await this.updateUserTrustScore(userId, -15 * Math.min(strikes, 4));
    } catch (error) {
      console.error('❌ Error issuing AI mislabeling strike:', error);
    }
  }

  /**
   * Gebruiker trekt bericht terug (binnen 24 uur)
   */
  async retractMessage(reportId: string, userId: string): Promise<boolean> {
    try {
      console.log('🔄 User retracting message for report:', reportId);

      const reportRef = doc(db, this.reportsCollection, reportId);
      await updateDoc(reportRef, {
        status: 'retracted',
        retractedAt: serverTimestamp()
      });

      // Update trust score positively (they did the right thing)
      await this.updateUserTrustScore(userId, 5);

      console.log('✅ Message retracted successfully');
      return true;

    } catch (error) {
      console.error('❌ Error retracting message:', error);
      return false;
    }
  }

  /**
   * Publiceer als public shaming (na 24 uur of direct bij ernstige gevallen)
   */
  async publishPublicShaming(reportId: string): Promise<string | null> {
    try {
      console.log('📢 Publishing public shaming for report:', reportId);

      // Get report details
      const reportQuery = query(
        collection(db, this.reportsCollection),
        where('__name__', '==', reportId)
      );
      const reportSnapshot = await getDocs(reportQuery);
      
      if (reportSnapshot.empty) {
        throw new Error('Report not found');
      }

      const reportData = reportSnapshot.docs[0].data() as Report;

      // Check if already published
      if (reportData.status === 'published') {
        console.log('Report already published');
        return null;
      }

      // Create public shaming entry
      const publicShaming: Omit<PublicShaming, 'id'> = {
        reportId,
        offenderUserId: reportData.reportedUserId,
        offenderUserName: reportData.reportedUserName,
        messageContent: reportData.messageContent,
        reportReason: reportData.reason,
        category: reportData.category,
        publishedAt: new Date(),
        reporterCount: await this.getReportCountForUser(reportData.reportedUserId),
        isVisible: true,
        pinnedUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 dagen gepind
      };

      const shamingRef = await addDoc(collection(db, this.publicShamingCollection), {
        ...publicShaming,
        publishedAt: serverTimestamp()
      });

      // Update report status
      await updateDoc(doc(db, this.reportsCollection, reportId), {
        status: 'published',
        publishedAt: serverTimestamp()
      });

      // Update user trust score negatively
      await this.updateUserTrustScore(reportData.reportedUserId, -20);

      // Check if user should be banned
      await this.checkForAutoBan(reportData.reportedUserId);

      console.log('✅ Public shaming published:', shamingRef.id);
      return shamingRef.id;

    } catch (error) {
      console.error('❌ Error publishing public shaming:', error);
      throw error;
    }
  }

  /**
   * Haal alle actieve public shamings op voor NewsFeed
   */
  async getActivePublicShamings(): Promise<PublicShaming[]> {
    try {
      const q = query(
        collection(db, this.publicShamingCollection),
        where('isVisible', '==', true),
        orderBy('publishedAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const shamings: PublicShaming[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        shamings.push({
          id: doc.id,
          ...data,
          publishedAt: data.publishedAt?.toDate() || new Date()
        } as PublicShaming);
      });

      return shamings;

    } catch (error) {
      console.error('❌ Error getting public shamings:', error);
      return [];
    }
  }

  /**
   * Haal reports op voor een gebruiker (als moderator)
   */
  async getReportsForUser(userId: string): Promise<Report[]> {
    try {
      const q = query(
        collection(db, this.reportsCollection),
        where('reportedUserId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const reports: Report[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        reports.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          warningExpiresAt: data.warningExpiresAt?.toDate(),
          retractedAt: data.retractedAt?.toDate(),
          publishedAt: data.publishedAt?.toDate()
        } as Report);
      });

      return reports;

    } catch (error) {
      console.error('❌ Error getting reports for user:', error);
      return [];
    }
  }

  /**
   * Verstuur waarschuwing naar gemelde gebruiker
   */
  private async sendWarningNotification(userId: string, reportId: string, messageContent: string): Promise<void> {
    try {
      // Add to user's inbox
      await addDoc(collection(db, 'user_inbox', userId, 'messages'), {
        type: 'warning',
        category: 'moderation',
        title: '⚠️ Waarschuwing: Ongepast Bericht Gemeld',
        message: `
Een van je berichten is gemeld als ongepast:

"${messageContent.substring(0, 200)}${messageContent.length > 200 ? '...' : ''}"

Je hebt 24 uur om dit bericht terug te trekken. Als je dit niet doet, wordt het bericht publiek gedeeld in de community feed als voorbeeld van ongepast gedrag.

Klik op "Bericht Terugtrekken" om je excuses aan te bieden en het bericht te verwijderen.
        `.trim(),
        isRead: false,
        priority: 'high',
        createdAt: serverTimestamp(),
        actionData: {
          actionType: 'retract_message',
          actionText: 'Bericht Terugtrekken',
          reportId: reportId
        }
      });

    } catch (error) {
      console.error('❌ Error sending warning notification:', error);
    }
  }

  /**
   * Plan automatische publicatie na 24 uur
   */
  private async scheduleAutoPublish(reportId: string, expiresAt: Date): Promise<void> {
    // In een echte implementatie zou je dit via Cloud Functions schedulen
    // Voor nu simuleren we dit met een setTimeout (alleen voor demo)
    console.log('⏰ Scheduled auto-publish for report:', reportId, 'at:', expiresAt);
    
    // TODO: Implementeer via Cloud Functions Scheduler
    // Dit zou een Cloud Function moeten zijn die elke uur draait en expired reports publiceert
  }

  /**
   * Update gebruiker rapport geschiedenis
   */
  private async updateUserReportHistory(userId: string, type: 'reporter' | 'reported'): Promise<void> {
    try {
      const historyRef = doc(db, this.userHistoryCollection, userId);
      
      if (type === 'reporter') {
        // Increment reports submitted
        const history = await this.getUserReportHistory(userId);
        await updateDoc(historyRef, {
          reportsSubmitted: (history?.reportsSubmitted || 0) + 1,
          lastReportAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // Increment reports received
        const history = await this.getUserReportHistory(userId);
        await updateDoc(historyRef, {
          reportsReceived: (history?.reportsReceived || 0) + 1,
          updatedAt: serverTimestamp()
        });
      }

    } catch (error) {
      console.error('❌ Error updating user report history:', error);
    }
  }

  /**
   * Haal gebruiker rapport geschiedenis op
   */
  private async getUserReportHistory(userId: string): Promise<UserReportHistory | null> {
    try {
      const historyRef = doc(db, this.userHistoryCollection, userId);
      const snapshot = await getDocs(query(collection(db, this.userHistoryCollection), where('userId', '==', userId)));
      
      if (snapshot.empty) return null;
      
      return snapshot.docs[0].data() as UserReportHistory;

    } catch (error) {
      console.error('❌ Error getting user report history:', error);
      return null;
    }
  }

  /**
   * Update gebruiker trust score
   */
  private async updateUserTrustScore(userId: string, change: number): Promise<void> {
    try {
      const history = await this.getUserReportHistory(userId);
      const currentScore = history?.trustScore || 50; // Start at 50
      const newScore = Math.max(0, Math.min(100, currentScore + change));

      await updateDoc(doc(db, this.userHistoryCollection, userId), {
        trustScore: newScore,
        updatedAt: serverTimestamp()
      });

      console.log(`Updated trust score for ${userId}: ${currentScore} -> ${newScore}`);

    } catch (error) {
      console.error('❌ Error updating trust score:', error);
    }
  }

  /**
   * Tel aantal reports voor een gebruiker
   */
  private async getReportCountForUser(userId: string): Promise<number> {
    try {
      const q = query(
        collection(db, this.reportsCollection),
        where('reportedUserId', '==', userId)
      );
      const snapshot = await getDocs(q);
      return snapshot.size;

    } catch (error) {
      console.error('❌ Error getting report count:', error);
      return 0;
    }
  }

  /**
   * Check of gebruiker automatisch geband moet worden
   */
  private async checkForAutoBan(userId: string): Promise<void> {
    try {
      const reportCount = await this.getReportCountForUser(userId);
      const settings = await this.getReportingSettings();
      
      if (reportCount >= settings.maxWarningsBeforeBan) {
        await this.banUser(userId, 'Automatische ban na meerdere meldingen');
      }

    } catch (error) {
      console.error('❌ Error checking for auto-ban:', error);
    }
  }

  /**
   * Ban een gebruiker
   */
  private async banUser(userId: string, reason: string): Promise<void> {
    try {
      const banExpiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 dagen

      await updateDoc(doc(db, this.userHistoryCollection, userId), {
        isBanned: true,
        banReason: reason,
        banExpiresAt: Timestamp.fromDate(banExpiresAt),
        trustScore: 0,
        updatedAt: serverTimestamp()
      });

      console.log(`🚫 User ${userId} has been banned: ${reason}`);

    } catch (error) {
      console.error('❌ Error banning user:', error);
    }
  }

  /**
   * Haal reporting instellingen op
   */
  private async getReportingSettings(): Promise<ReportingSettings> {
    try {
      const snapshot = await getDocs(collection(db, this.settingsCollection));
      
      if (!snapshot.empty) {
        return snapshot.docs[0].data() as ReportingSettings;
      }

      // Default settings
      return {
        autoPublishAfterHours: 24,
        maxWarningsBeforeBan: 3,
        shamingDurationDays: 7,
        minTrustScoreToReport: 20,
        falseReportPenalty: 10,
        enabledCategories: ['sexual_harassment', 'bullying', 'hate_speech', 'spam'],
        autoPublishCategories: ['sexual_harassment', 'hate_speech']
      };

    } catch (error) {
      console.error('❌ Error getting reporting settings:', error);
      return {
        autoPublishAfterHours: 24,
        maxWarningsBeforeBan: 3,
        shamingDurationDays: 7,
        minTrustScoreToReport: 20,
        falseReportPenalty: 10,
        enabledCategories: ['sexual_harassment', 'bullying', 'hate_speech', 'spam'],
        autoPublishCategories: ['sexual_harassment', 'hate_speech']
      };
    }
  }
}

export default new ReportingService();
export { ReportingService };
